import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import { PrismBanner, PrismCard, PrismToggle } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq, not } from 'ember-truth-helpers';
import { THEME } from 'frontend-utils/utils/prism/theme';
import DarkModeIllustration from 'lumen/assets/images/settings/themes/dark-mode.svg?raw';
import LightModeIllustration from 'lumen/assets/images/settings/themes/light-mode.svg?raw';
import SystemSettingsIllustration from 'lumen/assets/images/settings/themes/system-settings.svg?raw';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import SettingsThemeSelectionCard from 'lumen/components/settings/theme/selection-card';
import type DestinationSettingsThemeController from 'lumen/controllers/destination/settings/theme';

interface DestinationSettingsThemeSignature {
  Args: {
    controller: DestinationSettingsThemeController;
    model: unknown;
  };
}

<template>
  <SettingsPageTemplate
    @description={{t "settings.theme.description"}}
    @title={{t "settings.theme.title"}}
    @type="container"
    data-test-id="theme-settings-page"
  >

    <section class="flex flex-col gap-600">
      {{#unless @controller.theme.hasPermissions}}
        <PrismBanner
          @bannerMessage={{t "settings.theme.disabled.message"}}
          @bannerStyle="brand"
          @bannerTitle={{t "settings.theme.disabled.title"}}
          @link={{hash
            icon=true
            text=(t "general.learn_more")
            url="https://help.mylighthouse.com/en/articles/12807557-customizing-your-theme-in-lighthouse"
          }}
          data-test-id="theme-settings-disabled-banner"
        />
      {{/unless}}

      <PrismCard
        @contentLayout="noPadding"
        data-test-id="theme-settings-high-contrast-card"
      >
        <:content>
          <div class="flex flex-col gap-100 p-400">
            <PrismToggle
              @dataTestId="theme-high-contrast-toggle"
              @isChecked={{@controller.highContrast}}
              @isDisabled={{not @controller.theme.hasPermissions}}
              @label={{t "settings.theme.high_contrast.title"}}
              @labelPosition="after"
              @onChange={{@controller.toggleHighContrast}}
            />
            <p class="paragraph-300--regular text-neutral-subdued">{{t
                "settings.theme.high_contrast.description"
              }}</p>
          </div>
        </:content>
      </PrismCard>

      <section class="theme-selection flex grid-cols-3 justify-between gap-400">
        <SettingsThemeSelectionCard
          @checked={{eq @controller.themeValue THEME.LIGHT}}
          @imageAlt={{t "settings.theme.light"}}
          @imageHtml={{LightModeIllustration}}
          @label={{t "settings.theme.light"}}
          @onChange={{@controller.selectTheme}}
          @value={{THEME.LIGHT}}
        />
        <SettingsThemeSelectionCard
          @checked={{eq @controller.themeValue THEME.DARK}}
          @disabled={{not @controller.theme.hasPermissions}}
          @imageAlt={{t "settings.theme.dark"}}
          @imageHtml={{DarkModeIllustration}}
          @label={{t "settings.theme.dark"}}
          @onChange={{@controller.selectTheme}}
          @value={{THEME.DARK}}
        />
        <SettingsThemeSelectionCard
          @checked={{eq @controller.themeValue THEME.SYSTEM}}
          @disabled={{not @controller.theme.hasPermissions}}
          @imageAlt={{t "settings.theme.system"}}
          @imageHtml={{SystemSettingsIllustration}}
          @label={{t "settings.theme.system"}}
          @onChange={{@controller.selectTheme}}
          @value={{THEME.SYSTEM}}
        />
      </section>

      <footer>
        <p class="paragraph-300--regular text-neutral-subdued">{{t
            "settings.theme.system.description"
          }}</p>
      </footer>
    </section>

  </SettingsPageTemplate>
</template> satisfies TOC<DestinationSettingsThemeSignature>;
